import { makeObservable, observable, action, runInAction } from 'mobx';
import favoriteService from '../services/favoriteService';

class FavoriteStore {
  // Observable state
  favoriteProducts = [];
  favoriteServices = [];
  favoritePartners = [];
  favoriteProductIds = new Set();
  favoriteServiceIds = new Set();
  favoritePartnerIds = new Set();
  counts = { products: 0, services: 0, partners: 0, total: 0 };
  loading = false;
  error = null;

  constructor(rootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      favoriteProducts: observable,
      favoriteServices: observable,
      favoritePartners: observable,
      favoriteProductIds: observable,
      favoriteServiceIds: observable,
      favoritePartnerIds: observable,
      counts: observable,
      loading: observable,
      error: observable,
      fetchAllFavorites: action,
      toggleFavoriteProduct: action,
      toggleFavoriteService: action,
      toggleFavoritePartner: action,
      isProductFavorite: action,
      isServiceFavorite: action,
      isPartnerFavorite: action,
      clearError: action,
      reset: action
    });
  }

  /**
   * Fetch all favorites
   */
  async fetchAllFavorites() {
    this.loading = true;
    this.error = null;

    const result = await favoriteService.getAllFavorites();

    runInAction(() => {
      if (result.success) {
        this.favoriteProducts = result.data.products || [];
        this.favoriteServices = result.data.services || [];
        this.favoritePartners = result.data.partners || [];
        this.counts = result.data.counts || { products: 0, services: 0, partners: 0, total: 0 };

        // Update ID sets for quick lookups
        this.favoriteProductIds = new Set(this.favoriteProducts.map(f => f.product_id));
        this.favoriteServiceIds = new Set(this.favoriteServices.map(f => f.service_id));
        this.favoritePartnerIds = new Set(this.favoritePartners.map(f => f.partner_id));
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result.success;
  }

  /**
   * Check if a product is favorited
   */
  isProductFavorite(productId) {
    return this.favoriteProductIds.has(productId);
  }

  /**
   * Check if a service is favorited
   */
  isServiceFavorite(serviceId) {
    return this.favoriteServiceIds.has(serviceId);
  }

  /**
   * Check if a partner is favorited
   */
  isPartnerFavorite(partnerId) {
    return this.favoritePartnerIds.has(partnerId);
  }

  /**
   * Toggle favorite status for a product
   */
  async toggleFavoriteProduct(productId) {
    const isFavorite = this.favoriteProductIds.has(productId);

    let result;
    if (isFavorite) {
      result = await favoriteService.removeFavoriteProduct(productId);
      if (result.success) {
        runInAction(() => {
          this.favoriteProductIds.delete(productId);
          this.favoriteProducts = this.favoriteProducts.filter(f => f.product_id !== productId);
          this.counts.products--;
          this.counts.total--;
        });
      }
    } else {
      result = await favoriteService.addFavoriteProduct(productId);
      if (result.success) {
        runInAction(() => {
          this.favoriteProductIds.add(productId);
          this.counts.products++;
          this.counts.total++;
        });
      }
    }

    return { success: result.success, isFavorite: !isFavorite };
  }

  /**
   * Toggle favorite status for a service
   */
  async toggleFavoriteService(serviceId) {
    const isFavorite = this.favoriteServiceIds.has(serviceId);

    let result;
    if (isFavorite) {
      result = await favoriteService.removeFavoriteService(serviceId);
      if (result.success) {
        runInAction(() => {
          this.favoriteServiceIds.delete(serviceId);
          this.favoriteServices = this.favoriteServices.filter(f => f.service_id !== serviceId);
          this.counts.services--;
          this.counts.total--;
        });
      }
    } else {
      result = await favoriteService.addFavoriteService(serviceId);
      if (result.success) {
        runInAction(() => {
          this.favoriteServiceIds.add(serviceId);
          this.counts.services++;
          this.counts.total++;
        });
      }
    }

    return { success: result.success, isFavorite: !isFavorite };
  }

  /**
   * Toggle favorite status for a partner
   */
  async toggleFavoritePartner(partnerId) {
    const isFavorite = this.favoritePartnerIds.has(partnerId);

    let result;
    if (isFavorite) {
      result = await favoriteService.removeFavoritePartner(partnerId);
      if (result.success) {
        runInAction(() => {
          this.favoritePartnerIds.delete(partnerId);
          this.favoritePartners = this.favoritePartners.filter(f => f.partner_id !== partnerId);
          this.counts.partners--;
          this.counts.total--;
        });
      }
    } else {
      result = await favoriteService.addFavoritePartner(partnerId);
      if (result.success) {
        runInAction(() => {
          this.favoritePartnerIds.add(partnerId);
          this.counts.partners++;
          this.counts.total++;
        });
      }
    }

    return { success: result.success, isFavorite: !isFavorite };
  }

  /**
   * Clear error
   */
  clearError() {
    this.error = null;
  }

  /**
   * Reset store (on logout)
   */
  reset() {
    this.favoriteProducts = [];
    this.favoriteServices = [];
    this.favoritePartners = [];
    this.favoriteProductIds = new Set();
    this.favoriteServiceIds = new Set();
    this.favoritePartnerIds = new Set();
    this.counts = { products: 0, services: 0, partners: 0, total: 0 };
    this.loading = false;
    this.error = null;
  }
}

export default FavoriteStore;
